// EmbedSnippet.tsx — the widget's <script> tag plus its Copy button, extracted so it exists in
// exactly one place. Before this, `BotSettings.tsx` rendered the tag and its clipboard logic
// inline; Task 9 (Overview "brand new" hero, handoff README §3) needs the identical tag in a
// second spot. Two independently-typed renderings of the same fact is exactly the class of bug
// this project has been fixing all cycle (CLAUDE.md: the pricing page promised "No badge" while
// the widget rendered one) — so this is the one place the snippet string and the copy
// interaction are defined, and both call sites consume it.
//
// `variant` covers the three visual treatments the product actually uses, not a general
// theming API:
//   - 'card'    — BotSettings' existing Settings-page block: stacked code block, small outline
//                 Copy button below it, "Copied!" label. Unchanged from before this extraction.
//   - 'hero'    — the Overview brand-new hero (README §3): side-by-side code block + full-height
//                 primary Copy button, "✓ Copied" label, tighter radius.
//   - 'overlay' — onboarding's `GoLiveStep.tsx` final step: a `<pre>` block with the Copy button
//                 absolutely positioned over its top-right corner, "✓ Copied" label. Added (fix
//                 round 1) rather than leaving GoLiveStep's own third copy of this same snippet
//                 string + clipboard logic in place — its shape genuinely differs from both
//                 existing variants, so it earned a variant instead of another duplicate.
import { useState } from 'react';
import { Button } from '@repo/ui/components/button';
import { embedSnippet } from '../lib/embed-snippet';
import { useT } from '../locales/context';

export function EmbedSnippet({
  publicId,
  variant = 'card',
}: {
  publicId: string;
  variant?: 'card' | 'hero' | 'overlay';
}) {
  // Locale-dashboard plan, Task 11 — resolved via `useT()` directly, same idiom as
  // `WidgetPreview.tsx`'s own extraction in this batch: all three of this component's call
  // sites (`BotSettings.tsx` here, Overview's `FirstRun.tsx`, onboarding's `GoLiveStep.tsx`)
  // already call `useT()` for their own copy, but threading it through as a prop just for this
  // component's own three strings isn't worth touching those two already-shipped files for.
  const t = useT();
  const [copied, setCopied] = useState(false);
  const snippet = embedSnippet(publicId);

  async function handleCopy() {
    try {
      if (!navigator.clipboard) throw new Error('no clipboard API');
      await navigator.clipboard.writeText(snippet);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = snippet;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();
      try {
        document.execCommand('copy');
      } finally {
        document.body.removeChild(textarea);
      }
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  }

  if (variant === 'hero') {
    return (
      <div className="flex items-stretch gap-2">
        <code className="min-w-0 flex-1 overflow-x-auto rounded-md border bg-muted px-3.5 py-3 font-mono text-xs leading-relaxed whitespace-pre">
          {snippet}
        </code>
        <Button
          type="button"
          onClick={handleCopy}
          className="h-auto shrink-0 self-stretch px-4 text-[13px]"
        >
          {copied ? t.botSettings.embedCopiedCheck : t.botSettings.embedCopyCta}
        </Button>
      </div>
    );
  }

  if (variant === 'overlay') {
    return (
      // The button sits BESIDE the code, not over it. It was absolutely positioned on top,
      // which failed twice over: the snippet is one long unbroken line, so at scroll position 0
      // the text underneath the button is the MIDDLE of the string — reserving padding at the
      // end cannot help — and `outline`'s `dark:bg-input/30` is only 30% opaque, so in dark mode
      // the code showed straight through it. Both are properties of overlaying a control on a
      // horizontally scrolling region, so the fix is to stop doing that rather than keep
      // patching symptoms (Johnny, dev, 2026-08-12: "nut copy van dang de code" — after the
      // padding-and-opaque-background attempt had already shipped and still overlapped).
      <div className="flex items-start gap-2 rounded-[10px] bg-muted p-2">
        <pre className="min-w-0 flex-1 overflow-x-auto p-2 font-mono text-[12.5px]">{snippet}</pre>
        <Button type="button" variant="outline" size="sm" className="shrink-0" onClick={handleCopy}>
          {copied ? t.botSettings.embedCopiedCheck : t.botSettings.embedCopyCta}
        </Button>
      </div>
    );
  }

  return (
    <>
      <pre className="overflow-x-auto rounded-md bg-muted p-3 text-xs">{snippet}</pre>
      <div>
        <Button type="button" variant="outline" size="sm" onClick={handleCopy}>
          {copied ? t.botSettings.embedCopiedCard : t.botSettings.embedCopyCta}
        </Button>
      </div>
    </>
  );
}
